import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, PopoverShell, ProgressBar, TagGeneral, Text, TooltipTrigger, sparkToast } from '@spark';
import { GlobalContext } from '@/app/providers/globalState';
import { data_from_server_get, data_from_server_post } from '@/shared/http/client';
import { buildLabelGenWarningMessage } from '@/features/datasets/utils/labelGenerationWarning';
import { percentFromGenerationStatus } from '@/features/datasets/utils/generationProgressStatus';
import ActiveProgressCard from './ActiveProgressCard';

function generationProgressForUsecase(progressStatus, usecaseId) {
    if (!progressStatus || !usecaseId) return null;
    const match = Object.entries(progressStatus).find(([key, item]) => {
        if (!item?.isGeneration) return false;
        if (typeof item.progress !== 'number') return false;
        if (item.usecaseId && String(item.usecaseId) === String(usecaseId)) return true;
        return key === `ai_gen_${usecaseId}` || key.startsWith(`ai_gen_${usecaseId}__`);
    });
    return typeof match?.[1]?.progress === 'number' ? match[1].progress : null;
}

/**
 * Docked Improve & Retrain label-generation card (same chrome as
 * TrainingProgressBanner: Figma 3730:8169 / 3730:7924). Sparkles icon,
 * title + tag, progress or retry actions, trash with cancel confirm.
 */
const LabelGenerationContinueBanner = ({
    usecaseId,
    clientId,
    pending,
    onPendingChange,
}) => {
    const { progressStatus, registerStartedTraining } = useContext(GlobalContext) || {};
    const awaiting = Boolean(pending?.awaiting_continue);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [hydratedProgress, setHydratedProgress] = useState(0);
    const closeAreaRef = useRef(null);

    useEffect(() => {
        if (!confirmOpen) return undefined;
        const onMouseDown = (event) => {
            if (!closeAreaRef.current?.contains(event.target)) {
                setConfirmOpen(false);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape') setConfirmOpen(false);
        };
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [confirmOpen]);

    useEffect(() => {
        if (!usecaseId || awaiting) {
            return undefined;
        }
        let cancelled = false;
        data_from_server_get(`ai-generation/status/${usecaseId}`, (result) => {
            if (cancelled) return;
            setHydratedProgress(percentFromGenerationStatus(result));
        });
        return () => {
            cancelled = true;
        };
    }, [usecaseId, awaiting]);

    const warningMessage = buildLabelGenWarningMessage({
        failedPrompts: pending?.failed_prompts,
        totalPrompts: pending?.total_prompts,
        failureBreakdown: pending?.failure_breakdown,
        failureReason: pending?.failure_reason,
    });
    const liveProgress = generationProgressForUsecase(progressStatus, usecaseId);
    const displayProgress = liveProgress ?? hydratedProgress;

    const handleContinue = (retryFailed) => {
        if (!usecaseId || busy) return;
        setBusy(true);
        data_from_server_post(
            'continue-improve-retrain',
            { client_id: clientId, usecase_id: usecaseId, retry_failed: retryFailed },
            (result) => {
                setBusy(false);
                if (!result?.success) {
                    sparkToast.error(result?.detail || result?.message || 'Failed to continue Improve & Retrain');
                    return;
                }
                if (result.started_training) {
                    // Seed the global trainStatuses map so the docked
                    // TrainingProgressBanner swaps in immediately instead of
                    // waiting for the first WebSocket training update.
                    registerStartedTraining?.(result.distillation_event_id, usecaseId);
                    onPendingChange?.(null);
                    return;
                }
                onPendingChange?.(result.improve_retrain_pending || {
                    ...pending,
                    remaining_datasets: result.queued_datasets || [],
                    retry_datasets: [],
                    awaiting_continue: false,
                });
            },
            (errorDetail) => {
                setBusy(false);
                sparkToast.error(errorDetail || 'Failed to continue Improve & Retrain');
            },
        );
    };

    const handleAbort = () => {
        if (!usecaseId || busy) return;
        setConfirmOpen(false);
        setBusy(true);
        data_from_server_post(
            'abort-improve-retrain',
            { client_id: clientId, usecase_id: usecaseId },
            (result) => {
                setBusy(false);
                if (!result?.success) {
                    sparkToast.error(result?.detail || result?.message || 'Failed to cancel Improve & Retrain');
                    return;
                }
                onPendingChange?.(null);
            },
            (errorDetail) => {
                setBusy(false);
                sparkToast.error(errorDetail || 'Failed to cancel Improve & Retrain');
            },
        );
    };

    return (
        <ActiveProgressCard
            iconSlug="sparkles-2"
            title={awaiting ? 'Label generation incomplete' : 'Generating labels'}
            tag={<TagGeneral label={awaiting ? 'Waiting' : 'In progress'} />}
            percent={awaiting ? null : displayProgress}
            deleteAriaLabel="Delete label generation"
            confirmOpen={confirmOpen}
            onDeleteClick={() => setConfirmOpen((open) => !open)}
            closeAreaRef={closeAreaRef}
            confirmDialog={(
                <div className="training-progress-banner__confirm" role="dialog" aria-label="Cancel Improve & Retrain">
                    <PopoverShell
                        tip
                        tipDirection="bottom"
                        tipAlign="end"
                        className="training-progress-banner__confirm-shell"
                        panelClassName="training-progress-banner__confirm-panel"
                    >
                        <div className="training-progress-banner__confirm-body">
                            <Text variant="base-semibold" as="p">
                                Cancel Improve & Retrain?
                            </Text>
                            <Text variant="base-regular" color="secondary" as="p">
                                Label generation will stop. You can start Improve & Retrain again from Evaluations.
                            </Text>
                        </div>
                        <div className="training-progress-banner__confirm-actions">
                            <Button
                                variant="tertiary"
                                size="small"
                                type="button"
                                onClick={() => setConfirmOpen(false)}
                            >
                                Keep waiting
                            </Button>
                            <Button
                                variant="primary"
                                size="small"
                                danger
                                type="button"
                                disableWhenReadOnly
                                onClick={handleAbort}
                            >
                                Cancel
                            </Button>
                        </div>
                    </PopoverShell>
                </div>
            )}
        >
            {awaiting ? (
                <>
                    <Text variant="detail-regular" color="secondary" as="p" className="training-progress-banner__warning">
                        {warningMessage}
                    </Text>
                    <div className="training-progress-banner__choice-actions">
                        <TooltipTrigger
                            text="Generate labels only for records that failed, then train. Retried labels stay on the same origin_labels column for this version."
                            placement="bottom"
                        >
                            <Button
                                variant="primary"
                                size="small"
                                type="button"
                                disabled={busy}
                                disableWhenReadOnly
                                onClick={() => handleContinue(true)}
                            >
                                Retry & retrain
                            </Button>
                        </TooltipTrigger>
                        <TooltipTrigger
                            text="Skip failed records and train on the labels that were generated successfully."
                            placement="bottom"
                        >
                            <Button
                                variant="tertiary"
                                size="small"
                                type="button"
                                disabled={busy}
                                disableWhenReadOnly
                                onClick={() => handleContinue(false)}
                            >
                                Retrain
                            </Button>
                        </TooltipTrigger>
                    </div>
                </>
            ) : (
                <div className="training-progress-banner__progress">
                    <ProgressBar value={displayProgress} showPercentage />
                </div>
            )}
        </ActiveProgressCard>
    );
};

export default LabelGenerationContinueBanner;
